/**
 * Variant image grid
 *
 * Makes clickable variant thumbnails drive the existing <variant-selects>
 * custom element: clicking a tile updates the matching <select> (or radio
 * inputs) and dispatches a `change` event so the theme's built-in logic
 * handles price/image/url updates. Also keeps the grid in sync when the
 * user changes the variant via the dropdown directly.
 */
(function () {
  function findOptionControl(variantSelectsEl, optionPosition) {
    // Prefer the <select> for this option index (position is 1-based).
    const idx = optionPosition - 1;
    const selects = variantSelectsEl.querySelectorAll('select.select__select');
    if (selects[idx]) return { type: 'select', el: selects[idx] };

    // Otherwise fall back to the Nth fieldset of radios.
    const fieldsets = variantSelectsEl.querySelectorAll('fieldset.product-form__input');
    if (fieldsets[idx]) {
      return {
        type: 'radio',
        el: fieldsets[idx],
        inputs: fieldsets[idx].querySelectorAll('input[type="radio"]'),
      };
    }
    return null;
  }

  function setSelectValue(control, value) {
    if (!control) return false;

    if (control.type === 'select') {
      const opt = Array.from(control.el.options).find((o) => o.value === value);
      if (!opt) return false;
      control.el.value = value;
      control.el.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    if (control.type === 'radio') {
      let matched = null;
      control.inputs.forEach((input) => {
        if (input.value === value) {
          input.checked = true;
          matched = input;
        }
      });
      if (matched) {
        matched.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    return false;
  }

  function syncGridSelectionFromValue(gridEl, value) {
    const tiles = gridEl.querySelectorAll('.variant-image-grid__item');
    tiles.forEach((t) => {
      const isSel = t.getAttribute('data-value') === value;
      t.classList.toggle('is-selected', isSel);
      t.setAttribute('aria-checked', isSel ? 'true' : 'false');
    });
    const selectedLabel = gridEl.querySelector('[data-selected-value]');
    if (selectedLabel) selectedLabel.textContent = value;
  }

  // Delegate click handling on the document so it works even when product
  // markup is re-rendered (e.g. section reloads after variant change).
  document.addEventListener('click', function (event) {
    const tile = event.target.closest('.variant-image-grid__item');
    if (!tile) return;

    const grid = tile.closest('.variant-image-grid');
    const variantSelects = tile.closest('variant-selects');
    if (!grid || !variantSelects) return;

    const value = tile.getAttribute('data-value');
    const position = parseInt(grid.getAttribute('data-option-position'), 10) || 1;
    const control = findOptionControl(variantSelects, position);

    if (setSelectValue(control, value)) {
      syncGridSelectionFromValue(grid, value);
    }
  });

  // When the dropdown/radio changes for any reason, keep the grid in sync.
  document.addEventListener('change', function (event) {
    const target = event.target;
    if (!target || !(target.matches('select.select__select') || target.matches('input[type="radio"]'))) {
      return;
    }
    const variantSelects = target.closest('variant-selects');
    if (!variantSelects) return;

    const grids = variantSelects.querySelectorAll('.variant-image-grid');
    if (!grids.length) return;

    // Determine which option position this input represents.
    let position = null;
    if (target.matches('select.select__select')) {
      const selects = Array.from(variantSelects.querySelectorAll('select.select__select'));
      position = selects.indexOf(target) + 1;
    } else {
      const fieldsets = Array.from(variantSelects.querySelectorAll('fieldset.product-form__input'));
      const parentFieldset = target.closest('fieldset.product-form__input');
      position = fieldsets.indexOf(parentFieldset) + 1;
    }
    if (!position) return;

    const grid = Array.from(grids).find(
      (g) => parseInt(g.getAttribute('data-option-position'), 10) === position
    );
    if (!grid) return;

    const value = target.matches('select.select__select')
      ? target.value
      : target.value; // radio input's value

    syncGridSelectionFromValue(grid, value);
  });
})();
